import { isRecord, num, str } from './json'
import { FUEL_KINDS, type FuelKind, type Trip, type TripMeta, type Vehicle } from './types'

/**
 * Alles zum Fahrzeug einer Reise: lesen, schreiben, überführen - und der
 * Fuhrpark, aus dem sich eine neue Reise bedienen kann.
 *
 * Reine Funktionen, kein React, keine Datenbank. Die Reisen des Geräts holt
 * data/db.ts, die Formulare stehen in features/data; hier stehen nur die
 * Regeln, und die sind ohne Browser prüfbar.
 *
 * ## Warum es diese Datei gibt
 *
 * Die App wurde für ein einziges Fahrzeug gebaut, und das stand an fünf
 * Stellen im Programm statt an einer im Dokument: die 14 Liter des Wasserboxers
 * als empfohlener Verbrauch, der Zuschlag von 25 Prozent für den beladenen
 * Camper, die Ausgabenkategorie „Benzin“, das Nationalitätszeichen CH im Kopf
 * der App und dasselbe noch einmal auf dem Buchrücken. Jede dieser fünf Stellen
 * war für die erste Reise richtig und für jede zweite falsch.
 */

/** Wie die Kraftstoffart in der Oberfläche heisst. */
export const FUEL_LABEL: Record<FuelKind, string> = {
  benzin: 'Benzin',
  diesel: 'Diesel',
  gas: 'Gas (LPG/CNG)',
  strom: 'Strom',
}

/**
 * Die Einheit, in der Verbrauch und Tankinhalt dieser Art stehen.
 *
 * Ein Elektroauto verbraucht keine Liter. Die Einheit steht deshalb neben jedem
 * Feld, das eine Menge zeigt, und die Zahl selbst bleibt, was sie ist: eine
 * Menge je 100 km, beziehungsweise ein Vorrat.
 */
export const FUEL_UNIT: Record<FuelKind, string> = {
  benzin: 'l',
  diesel: 'l',
  gas: 'l',
  strom: 'kWh',
}

/**
 * Wie die Kraftstoffkategorie im Budget heisst.
 *
 * Sie ist der Grund, aus dem die Kraftstoffart überhaupt im Modell steht: die
 * Kategorienliste hiess fest „Benzin“, weil das Fahrzeug der ersten Reise einer
 * ist, und ein Diesel oder ein Elektroauto buchte seinen Strom unter „Benzin“
 * oder unter „Sonstiges“. Siehe categories() in lib/seed.ts.
 */
export const FUEL_CATEGORY: Record<FuelKind, string> = {
  benzin: 'Benzin',
  diesel: 'Diesel',
  gas: 'Gas',
  strom: 'Strom',
}

/** Ob ein Wert aus einer fremden Quelle eine Kraftstoffart ist. */
export function istKraftstoff(v: unknown): v is FuelKind {
  return typeof v === 'string' && (FUEL_KINDS as string[]).includes(v)
}

/**
 * Das Fahrzeug, mit dem jede Reise ohne eigenes Fahrzeugfeld unterwegs war.
 *
 * Nicht irgendein Vorgabewert, sondern die Beschreibung dessen, was das
 * Programm bis hierher fest angenommen hat: ein Benziner, ein Viertel Zuschlag
 * auf die Fahrzeit des Routendienstes, das Schild CH. Genau das - und nicht
 * etwa Nullen - kommt in eine bestehende Reise, damit sie sich nach dem Umbau
 * verhält wie davor.
 *
 * Eine frisch angelegte Reise bekommt es ausdrücklich nicht: sie startet mit
 * KEIN_FAHRZEUG (siehe EMPTY_TRIP in lib/trip.ts), also ohne Zuschlag und ohne
 * Schild. Wer mit einem Personenwagen plant, soll nicht ein Viertel zu langsam
 * planen, nur weil einmal ein T3 hier stand.
 */
export const ALTER_ZUSCHLAG = 0.25
const ALTES_SCHILD = 'CH'
const ALTE_ART: FuelKind = 'benzin'

/**
 * Das Fahrzeug einer Reise, die noch keines hat.
 *
 * Leer in allem, was niemand gesagt hat - kein Name, kein Verbrauch, kein
 * Zuschlag, kein Schild. Die Kraftstoffart muss dagegen etwas sein: sie
 * benennt die Kategorie, unter der getankt wird, und „unbekannt“ wäre dort
 * keine Kategorie. Benzin ist die häufigste und dieselbe wie bisher, also
 * ändert sich für niemanden etwas, der sie nie anfasst.
 */
export const KEIN_FAHRZEUG: Vehicle = {
  name: '',
  fuel: ALTE_ART,
  consumption: 0,
  tank: 0,
  surcharge: 0,
  sign: '',
}

/**
 * Das Fahrzeug, das eine Reise ohne Fahrzeugfeld beschreibt.
 *
 * Name und Verbrauch stehen dort, wo sie immer standen; alles Übrige ist das,
 * was im Programm fest verdrahtet war.
 *
 * Gelesen wird auch hier über json.ts und nicht unmittelbar: die Rahmendaten
 * kommen aus derselben ungeprüften Quelle wie das Fahrzeugfeld daneben - siehe
 * die Begründung bei fahrzeug().
 */
export function altesFahrzeug(meta: TripMeta): Vehicle {
  return {
    name: str(meta.vehicle),
    fuel: ALTE_ART,
    consumption: Math.max(0, num(meta.consumption, 0)),
    tank: 0,
    surcharge: ALTER_ZUSCHLAG,
    sign: ALTES_SCHILD,
  }
}

/**
 * Das Fahrzeug einer Reise - immer eines, auch wenn im Dokument keines steht.
 *
 * Die eine Stelle, an der gelesen wird. Wer meta.vehicle oder meta.consumption
 * unmittelbar anfasst, übersieht entweder das Fahrzeug oder den Altbestand;
 * hier ist beides zusammengeführt und nur hier.
 *
 * ## Warum hier geprüft wird und nicht beim Aufrufer
 *
 * Weil das Reisedokument aus einer Quelle kommt, die niemand kennt: aus dem
 * Abgleich, aus der lokalen Datenbank, aus einer Datei. Der Datei-Import lief
 * durch leseFahrzeug, der Abgleich nicht - er reichte den Rumpf als Trip
 * weiter, und damit reichte ein Feld, das diese Fassung nicht vorgesehen hat,
 * bis in die Rechnungen: eine Kraftstoffart „hybrid“ - eine künftige Fassung
 * der App, eine Handkorrektur am Server - liess die Reise nicht mehr öffnen,
 * weil die Kategorientabelle sie nicht kennt, und ein Zuschlag von 25 statt
 * 0.25 machte aus vier Fahrstunden 104. Eine PWA mischt Fassungen ausdrücklich
 * - das ist kein Papierfall.
 *
 * Geprüft wird deshalb an der Lesestelle und nicht an den Eintrittsstellen:
 * eine Eintrittsstelle kann man vergessen, diese eine nicht.
 *
 * ## Warum die alten Felder gewinnen dürfen
 *
 * Weicht die Kopie in den Rahmendaten vom Fahrzeug ab, gilt die Kopie. Sie ist
 * das Einzige, was eine ältere Fassung der App schreiben kann (sie kennt
 * Trip.vehicle nicht und reicht es unverändert weiter), und diese Fassung hält
 * beide immer gleich - siehe mitFahrzeug. Eine Abweichung ist damit kein
 * Widerspruch, den man auflösen müsste, sondern eine Auskunft: hier hat zuletzt
 * die ältere Fassung geschrieben. Wer nach dem ersten Tanken auf dem zweiten
 * Gerät 16 statt 14 Liter einträgt, rechnet danach überall mit 16, und der
 * nächste Schreibvorgang am Fahrzeug übernimmt sie, statt sie zu überschreiben.
 *
 * Leer ist dabei keine Abweichung, sondern keine Auskunft. Eine 0 im Verbrauch
 * und ein leerer Name heissen in diesem Programm überall „nicht eingetragen“,
 * und ein Dokument, das sein Fahrzeug ohne die alten Felder mitbringt - eine
 * künftige Fassung ohne die Kopie, ein von Hand angelegter Rumpf -, ist kein
 * Widerspruch, sondern eines, das die Kopie nicht kennt. Es verlöre sonst
 * Namen und Kraftstoffschätzung an ein Feld, das nie jemand gefüllt hat.
 */
export function fahrzeug(trip: Trip): Vehicle {
  const gelesen = leseFahrzeug(trip.vehicle)
  if (!gelesen) return altesFahrzeug(trip.meta)
  return { ...gelesen, ...ausDenAltenFeldern(gelesen, trip.meta) }
}

/** Was die Kopie in den Rahmendaten am Fahrzeug ändert - meistens nichts. */
function ausDenAltenFeldern(auto: Vehicle, meta: TripMeta): Partial<Vehicle> {
  const name = str(meta.vehicle)
  const consumption = Math.max(0, num(meta.consumption, 0))
  return {
    ...(name !== '' && name !== auto.name ? { name } : {}),
    ...(consumption > 0 && consumption !== auto.consumption ? { consumption } : {}),
  }
}

/**
 * Ob das Reisedokument aus der Zeit vor dem Fahrzeug stammt.
 *
 * Die Frage nach dem Dokument und nicht nach dem Zustand: das Fehlen des Felds
 * ist die einzige Auskunft darüber, wann eine Reise geschrieben wurde. Ein
 * vorhandenes, aber unlesbares Feld stammt nicht aus dieser Zeit: Es belegt,
 * dass die Reise ein Fahrzeug trägt, auch wenn dessen Angaben nicht lesbar
 * sind. fahrzeug() fällt dort weiterhin sicher auf die alten Felder zurück.
 */
export function ohneFahrzeug(trip: Trip): boolean {
  return !Object.prototype.hasOwnProperty.call(trip, 'vehicle')
}

/**
 * Die Reise mit einem anderen Fahrzeug.
 *
 * Name und Verbrauch gehen zusätzlich in die alten Felder. Das ist eine Kopie,
 * und sie ist mit Absicht da: auf einem zweiten Gerät kann für Tage noch die
 * vorige Fassung der App laufen - eine PWA hält sich im Cache -, und die liest
 * den Namen und den Verbrauch nur von dort. Ohne die Kopie stünde dort nach
 * dem ersten Abgleich eine Reise ohne Fahrzeug, mit einer Kraftstoffschätzung
 * von null.
 *
 * Geschrieben wird die Kopie ausschliesslich hier, und hier gehen beide immer
 * gemeinsam hinaus. Dass sie deswegen nicht auseinanderlaufen könnten, gilt
 * allerdings nur innerhalb dieser Fassung: die ältere schreibt meta.consumption
 * über ihr eigenes setMeta weiter, und genau dafür ist die Kopie ja da. Wer
 * dort nach dem ersten Tanken 16 Liter einträgt, hinterlässt eine Abweichung -
 * und die aufzulösen ist Sache der Lesestelle, siehe fahrzeug().
 */
export function mitFahrzeug(trip: Trip, neu: Vehicle): Trip {
  return {
    ...trip,
    vehicle: neu,
    meta: { ...trip.meta, vehicle: neu.name, consumption: neu.consumption },
  }
}

/**
 * Überführt eine Reise aus der Zeit vor dem Fahrzeug.
 *
 * @returns die Reise mit ihrem Fahrzeug, oder null wenn es nichts zu tun gibt.
 *
 * Zweimal ausgeführt entsteht kein zweites Fahrzeug: der zweite Durchgang
 * sieht ein Fahrzeug und gibt null zurück. Dieselbe Bauart wie bei
 * migrateExpense in lib/trip.ts und bei der Überführung des alten
 * Tagebuchtexts - ein Fixpunkt, den auch zwei gleichzeitig laufende Geräte
 * nicht verdoppeln können.
 *
 * Übernommen wird, was dasteht, und erfunden wird nichts: Name und Verbrauch
 * der Reise, dazu die Annahmen, mit denen sie bis hierher gerechnet wurde.
 * Damit trägt die Balkanreise danach ein Fahrzeug „VW T3 Camper“ mit 14 Litern
 * und 25 Prozent Zuschlag, ohne dass jemand etwas tut.
 */
export function ueberfuehreFahrzeug(trip: Trip): Trip | null {
  // Anders als ohneFahrzeug fragt diese Überführung nach dem Zustand: Auch ein
  // vorhandenes, aber unlesbares Feld muss durch ein brauchbares Fahrzeug
  // ersetzt werden, sonst bliebe die Reise dauerhaft auf den alten Feldern.
  if (leseFahrzeug(trip.vehicle)) return null
  return { ...trip, vehicle: altesFahrzeug(trip.meta) }
}

/**
 * Wie weit das Fahrzeug mit einer Füllung kommt, in Kilometern.
 *
 * 0 heisst „lässt sich nicht sagen“ - ohne Tankinhalt oder ohne Verbrauch gibt
 * es keine Reichweite, und eine Zahl aus einer Null wäre eine Behauptung. Die
 * Rechnung ist die einfache: Vorrat geteilt durch Verbrauch je 100 km.
 */
export function reichweiteKm(v: Vehicle): number {
  if (!(v.tank > 0) || !(v.consumption > 0)) return 0
  return Math.round((v.tank / v.consumption) * 100)
}

/**
 * Wie ein eingetragener Verbrauch zur Kraftstoffart passt.
 *
 * Hier stand einmal eine Empfehlung: 14 Liter, der Wert des Wasserboxers, und
 * jede Reise darunter bekam den Hinweis, sie liege für einen Benziner zu
 * niedrig - samt Knopf, der die 14 übernahm. Für einen modernen Diesel mit 5,5
 * Litern war das dauerhaft falsch, und der Knopf verdreifachte die
 * Kraftstoffschätzung.
 *
 * Deshalb wird hier nichts mehr empfohlen. Geprüft wird nur noch, ob die Zahl
 * überhaupt die eines Autos sein kann; die Grenzen sind weit und sollen es
 * sein - ein Zweiliter-Diesel wie ein durstiger Bus liegen dazwischen. Was
 * anschlägt, ist der vertippte Dezimalpunkt, und der ist es wert: an dieser
 * Zahl hängt die ganze Kraftstoffschätzung im Budget.
 *
 * Ein Wert von 0 zählt nicht mit: so sieht ein Feld aus, in dem gerade getippt
 * wird, und dabei soll kein Hinweis aufblitzen.
 */
export type VerbrauchUrteil = 'passt' | 'niedrig' | 'hoch'

/** Die Bandbreite je Kraftstoffart, in Litern beziehungsweise kWh je 100 km. */
const VERBRAUCH_BAND: Record<FuelKind, [number, number]> = {
  benzin: [2, 40],
  diesel: [2, 40],
  gas: [3, 50],
  strom: [8, 60],
}

export function verbrauchUrteil(v: Vehicle): VerbrauchUrteil {
  const wert = v.consumption
  if (!Number.isFinite(wert) || wert <= 0) return 'passt'
  const [unten, oben] = VERBRAUCH_BAND[v.fuel]
  if (wert < unten) return 'niedrig'
  if (wert > oben) return 'hoch'
  return 'passt'
}

/** Ein Fahrzeug, das auf diesem Gerät schon einmal eingetragen wurde. */
export interface FuhrparkEintrag {
  /** Aus welcher Reise es stammt - ihr Titel, oder leer wenn sie keinen hat. */
  reise: string
  fahrzeug: Vehicle
}

/**
 * Ein Reisedokument, wie es lokal liegt: die Reise-ID und der ungeprüfte Rumpf.
 */
export interface Reisebestand {
  tripId: string
  body: unknown
}

/**
 * Der Fuhrpark: die Fahrzeuge der anderen Reisen dieses Geräts.
 *
 * ## Warum aus den Reisen und nicht aus einer eigenen Ablage
 *
 * Weil eine eigene Ablage eine zweite Wahrheit wäre. Ein Fahrzeug, das in einer
 * Liste steht und in keiner Reise, ist ein Karteileichnam; eines, das in einer
 * Reise geändert wurde und in der Liste alt bleibt, ist schlimmer - man wählt
 * es aus und bekommt Werte, die niemand mehr fährt. Was hier entsteht, kann
 * nicht veralten: es ist der Bestand selbst, nur anders angesehen.
 *
 * Bezahlt wird das mit einer Grenze, und die ist ehrlich zu nennen: ein Gerät
 * kennt nur die Reisen, die auf ihm schon offen waren, denn nur die haben hier
 * eine Datenbank (siehe data/db.ts). Wer die zweite Reise auf einem frischen
 * Telefon anlegt, findet den Fuhrpark leer, bis er die erste einmal geöffnet
 * hat. Das ist der richtige Tausch: die Liste ist eine Bequemlichkeit, und eine
 * leere Liste kostet einen Tippvorgang - eine falsche kostet eine
 * Kraftstoffschätzung, der niemand ansieht, dass sie zum falschen Auto gehört.
 *
 * @param bestaende alle Reisen dieses Geräts mit ihrem Reisedokument.
 * @param ohne die offene Reise: ihr eigenes Fahrzeug ist kein Vorschlag.
 *
 * Doppelte fallen heraus - dasselbe Fahrzeug in drei Reisen ist eine Zeile,
 * nicht drei. Namenlose ebenfalls: eine Reise, in der noch niemand ein
 * Fahrzeug eingetragen hat, hat keines anzubieten.
 */
export function fuhrpark(bestaende: readonly Reisebestand[], ohne: string): FuhrparkEintrag[] {
  const gesehen = new Set<string>()
  const out: FuhrparkEintrag[] = []

  for (const bestand of bestaende) {
    if (bestand.tripId === ohne) continue
    const trip = leseReise(bestand.body)
    if (!trip) continue
    const auto = fahrzeug(trip)
    if (auto.name.trim() === '') continue

    const schluessel = JSON.stringify([
      auto.name,
      auto.fuel,
      auto.consumption,
      auto.tank,
      auto.surcharge,
      auto.sign,
    ])
    if (gesehen.has(schluessel)) continue
    gesehen.add(schluessel)
    out.push({ reise: trip.meta.title, fahrzeug: auto })
  }

  // Eine feste Reihenfolge, damit die Knopfreihe nicht bei jedem Öffnen anders
  // aussieht: die Reihenfolge der Datenbanken ist keine, auf die man sich
  // verlassen darf.
  return out.sort((a, b) =>
    a.fahrzeug.name === b.fahrzeug.name
      ? a.reise.localeCompare(b.reise)
      : a.fahrzeug.name.localeCompare(b.fahrzeug.name),
  )
}

/**
 * So viel von einem fremden Reisedokument, wie der Fuhrpark braucht: Titel,
 * die alten Fahrzeugfelder und das Fahrzeug selbst.
 *
 * Ungeprüft ist hier alles - der Rumpf kommt aus der Datenbank einer anderen
 * Reise und kann aus jeder Fassung dieser App stammen.
 */
function leseReise(body: unknown): Trip | null {
  if (!isRecord(body)) return null
  const meta = isRecord(body.meta) ? body.meta : {}
  const gelesen = leseFahrzeug(body.vehicle)
  return {
    meta: {
      title: str(meta.title),
      vehicle: str(meta.vehicle),
      crew: num(meta.crew, 1),
      start: str(meta.start),
      consumption: num(meta.consumption, 0),
      fuelPrice: num(meta.fuelPrice, 0),
      currency: str(meta.currency),
    },
    ...(gelesen ? { vehicle: gelesen } : {}),
    stages: [],
  }
}

/**
 * Ein gespeichertes Fahrzeug aus einer fremden Quelle - einer Importdatei, dem
 * Dokument einer anderen Reise, dem Abgleich.
 *
 * Fehlt es, ist es keines: dann greift der Rückfall auf die alten Felder, und
 * das ist die richtige Auskunft über ein Dokument aus jener Zeit. Eine
 * unbekannte Kraftstoffart wird zu Benzin und verwirft nicht das ganze
 * Fahrzeug - anders als bei einer Tulpe ist hier nichts zu behaupten, was in
 * die Irre führt: die Art benennt eine Kategorie und eine Einheit, und Benzin
 * ist die Art, mit der bis hierher jede Reise gerechnet hat.
 */
export function leseFahrzeug(v: unknown): Vehicle | undefined {
  if (!isRecord(v)) return undefined
  return {
    name: str(v.name),
    fuel: istKraftstoff(v.fuel) ? v.fuel : ALTE_ART,
    consumption: Math.max(0, num(v.consumption, 0)),
    tank: Math.max(0, num(v.tank, 0)),
    // Ein Zuschlag ist ein Anteil und kein Prozentwert: eine 25 in diesem Feld
    // wäre das Fünfundzwanzigfache der Fahrzeit. Nach oben deshalb eine Grenze,
    // die grosszügig ist und trotzdem keine Zahl ohne Bedeutung durchlässt.
    surcharge: Math.min(2, Math.max(0, num(v.surcharge, 0))),
    sign: str(v.sign),
  }
}
